import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer } from "@/components/Page";
import { StatusBadge, CategoryBadge, STATUSES } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Send } from "lucide-react";
import { API_BASE } from "@/lib/api";

function attachmentSrc(a) {
  // Back-compat: legacy tickets stored base64 data URLs directly
  if (typeof a === "string" && a.startsWith("data:")) return a;
  // New: attachment ID served via backend
  return `${API_BASE}/attachments/${a}`;
}

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, { status });
      setTicket(data);
      toast.success("Status updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/tickets/${id}/comments`, { body: comment });
      setTicket(data); setComment("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPosting(false); }
  };

  const canManage = user?.role === "estate_manager" || user?.role === "super_admin";

  if (loading) return <AppShell><PageContainer><div className="label-eyebrow">Loading…</div></PageContainer></AppShell>;
  if (!ticket) return <AppShell><PageContainer><div>Ticket not found.</div></PageContainer></AppShell>;

  return (
    <AppShell>
      <PageContainer>
        <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-zinc-600 hover:text-black mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-zinc-200 p-6 sm:p-8" data-testid="ticket-header">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <CategoryBadge category={ticket.category} />
                <StatusBadge status={ticket.status} />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter leading-tight break-words" data-testid="ticket-title">{ticket.title}</h1>
              <p className="mt-4 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed" data-testid="ticket-description">{ticket.description}</p>

              {ticket.attachments?.length > 0 && (
                <div className="mt-6">
                  <div className="label-eyebrow mb-3">Attachments</div>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {ticket.attachments.map((src, i) => (
                      <a key={i} href={attachmentSrc(src)} target="_blank" rel="noreferrer" className="border border-zinc-200 block" data-testid={`attach-${i}`}>
                        <img src={attachmentSrc(src)} alt="" className="w-full h-24 object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="bg-white border border-zinc-200">
              <div className="p-4 border-b border-zinc-200 label-eyebrow">Comments ({ticket.comments?.length || 0})</div>
              <div className="divide-y divide-zinc-100">
                {(ticket.comments || []).map((c) => (
                  <div key={c.id} className="p-4" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-semibold text-sm">{c.author?.name || "Unknown"}</div>
                      <span className="label-eyebrow">{c.author?.role === "super_admin" ? "Super Admin" : c.author?.role === "estate_manager" ? "Manager" : "Resident"}</span>
                      <div className="text-xs text-zinc-400 ml-auto">{new Date(c.at).toLocaleString()}</div>
                    </div>
                    <div className="text-sm text-zinc-700 whitespace-pre-wrap">{c.body}</div>
                  </div>
                ))}
                {(!ticket.comments || ticket.comments.length === 0) && (
                  <div className="p-8 text-center text-sm text-zinc-500">No comments yet.</div>
                )}
              </div>
              <form onSubmit={postComment} className="p-4 border-t border-zinc-200 bg-zinc-50" data-testid="comment-form">
                <Textarea required rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="comment-input" placeholder="Add a comment…" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black bg-white" />
                <Button type="submit" disabled={posting} data-testid="submit-comment-button" className="mt-3 rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">
                  <Send className="h-3.5 w-3.5" /> Post Comment
                </Button>
              </form>
            </div>
          </div>

          {/* Right: metadata */}
          <div className="space-y-4">
            <div className="bg-white border border-zinc-200 p-6" data-testid="ticket-meta">
              <div className="label-eyebrow mb-4">Details</div>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-xs text-zinc-500 uppercase tracking-wider">Estate</dt>
                  <dd className="mt-0.5 font-medium">{ticket.estate?.name || "—"}</dd>
                </div>
                {ticket.unit && (
                  <div data-testid="ticket-unit-details">
                    <dt className="text-xs text-zinc-500 uppercase tracking-wider">Unit</dt>
                    <dd className="mt-0.5 font-medium">
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        <div className="border border-zinc-200 p-2">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Block</div>
                          <div className="text-sm font-semibold mt-0.5" data-testid="ticket-unit-block">{ticket.unit.block || "—"}</div>
                        </div>
                        <div className="border border-zinc-200 p-2">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">House #</div>
                          <div className="text-sm font-semibold mt-0.5" data-testid="ticket-unit-house">{ticket.unit.house_number || "—"}</div>
                        </div>
                        <div className="border border-zinc-200 p-2">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Apt #</div>
                          <div className="text-sm font-semibold mt-0.5" data-testid="ticket-unit-apt">{ticket.unit.apartment_number || "—"}</div>
                        </div>
                      </div>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-zinc-500 uppercase tracking-wider">Raised by</dt>
                  <dd className="mt-0.5 font-medium">{ticket.raised_by?.name || "—"}</dd>
                </div>
                {ticket.on_behalf_of_resident && (
                  <div>
                    <dt className="text-xs text-zinc-500 uppercase tracking-wider">On behalf of</dt>
                    <dd className="mt-0.5 font-medium">{ticket.on_behalf_of_resident.name}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-zinc-500 uppercase tracking-wider">Assigned Manager</dt>
                  <dd className="mt-0.5 font-medium">{ticket.assigned_manager?.name || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-zinc-500 uppercase tracking-wider">Created</dt>
                  <dd className="mt-0.5 font-medium">{new Date(ticket.created_at).toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-xs text-zinc-500 uppercase tracking-wider">Last Update</dt>
                  <dd className="mt-0.5 font-medium">{new Date(ticket.updated_at).toLocaleString()}</dd>
                </div>
              </dl>
            </div>

            {canManage && (
              <div className="bg-white border border-zinc-200 p-6" data-testid="ticket-status-controls">
                <div className="label-eyebrow mb-4">Update Status</div>
                <Select value={ticket.status} onValueChange={changeStatus}>
                  <SelectTrigger className="rounded-none border-zinc-300" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="bg-white border border-zinc-200 p-6" data-testid="ticket-history">
              <div className="label-eyebrow mb-4">Status History</div>
              <ol className="space-y-3 text-sm">
                {(ticket.status_history || []).map((h, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 h-1.5 w-1.5 bg-black shrink-0" />
                    <div>
                      <div className="font-medium">{h.status.replace("_", " ")}</div>
                      <div className="text-xs text-zinc-500">{new Date(h.at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </PageContainer>
    </AppShell>
  );
}
